import React, { useContext, useState, useRef } from "react";
import styled from "styled-components";
import {
    Button,
    LottieLoadingIndicator,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModal,
    StandardModalSection,
    StandardModalSectionHeader,
    StandardModalSectionTitle,
    Toast,
    ImageCropModal,
    VideoCropModal
} from "src/components";
import { TextLabel, RemoveMediaButton } from "../style";
import ContentForm from "../forms/ContentForm";
import UploadMedia from "../forms/UploadMedia";
import { BodyAndMindContext } from "../../BodyAndMindContext";
import { s3PresignedContentUrl, saveContentWithImage, saveContentWithVideo, saveAttachments } from "../../api";
import { LoadingIndicatorButton } from "src/scenes/Survey/components/Utils";
import CloseIcon from "src/img/CloseButton.png";
import { isBase64Image, isBase64Video } from "../utils/VideoUtils";
import { emptyContentHtml } from "src/components/RichTextEditor/adapters";
import { htmlToCaption } from "src/components/RichTextEditor/htmlToCaption";
import { ASPECT_RATIO_LABEL } from "src/scenes/Noticeboard/constants";
import Attachment from "./Attachment";
import { DirectorySelection } from "./DirectorySelection";
import BodyAndMindCheckInSection from "./BodyAndMindCheckInSection";
import { UserContext } from "src/scenes/App/UserContext";
import { FEATURE_FLAGS, hasFeature } from "src/constants/FeatureFlag";
import {
    hasValidCheckIn,
    normaliseCheckInForRequest
} from "src/components/CheckIn/checkIn";

const AddContentFormId = "body-and-mind-add-content-form";

const AddContentForm = styled.form`
    width: 100%;
    min-width: 0;
    max-width: 100%;
    height: auto;
    max-height: inherit;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    overflow: visible;
    box-sizing: border-box;
    padding-bottom: 2rem;

    &::after {
        content: "";
        display: block;
        flex: 0 0 0.5rem;
    }
`;

const AddContentPrimaryGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 21rem);
    gap: 1rem;
    align-items: start;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const MediaColumn = styled.div`
    min-width: 0;
    max-width: 19rem;
    margin-right: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .upload-media {
        width: 100%;
    }

    @media (max-width: 900px) {
        max-width: 100%;
        margin-right: 0;
    }
`;

const CategoryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

function AddContent(props) {
    const uploadMediaRef = useRef(null);
    const { subcategoriesList, subcategoriesListIsFetching } = useContext(BodyAndMindContext);
    const user = useContext(UserContext);
    const canUseCheckedIn = hasFeature(user, FEATURE_FLAGS.CHECKED_IN);

    const { toggleAddContentModal, companyCategoriesList } = props;
    const [title, setTitle] = useState("");
    const [htmlDescription, setHtmlDescription] = useState(emptyContentHtml);
    const [uploadFile, setUploadFile] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState("category");
    const [selectedSubcategory, setSelectedSubcategory] = useState("subcategory");
    const [selectedTopic, setSelectedTopic] = useState("topic");
    const [selectedSubtopic, setSelectedSubtopic] = useState("subtopic");
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [dimensions, setDimensions] = useState({ width: null, height: null });
    const [fileType, setFileType] = useState("");
    const [base64ImageToCrop, setBase64ImageToCrop] = useState(null);
    const [base64VideoFileToCrop, setBase64VideoFileToCrop] = useState(null);
    const [useCrop, setUseCrop] = useState([]);

    // Crop Image
    const [croppedBlob, setCroppedBlob] = useState(null);
    const [croppedVideo, setCroppedVideo] = useState(null);
    const [showCropperModal, setShowCropperModal] = useState(false);

    const [uploadedAttachmentFiles, setUploadedAttachmentFiles] = useState([]);
    const [deleteAttachmentFiles, setDeleteAttachmentFiles] = useState([]);
    const [checkIn, setCheckIn] = useState(null);

    const onImageCropComplete = (file, width, height) => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        if (uploadFile) {
            URL.revokeObjectURL(uploadFile);
        }

        setUploadFile(url); // For preview
        setDimensions({ width, height });
        setCroppedBlob(file);
        setShowCropperModal(false);
    };

    const onVideoCropComplete = (file, crop, trimStart, trimEnd, aspectRatioLabel) => {
        if (!file) {
            return;
        }
        setCroppedVideo(file);
        setBase64ImageToCrop(null);
        setBase64VideoFileToCrop(null);
        setUseCrop((prev) => ({
            ...prev,
            trimStart,
            trimEnd,
            aspectRatio: aspectRatioLabel,
            crop: crop,
        }));
    };

    const setOnUploadFileChange = (file) => {
        setUploadFile(file);
        setErrors({ ...errors, uploadFile: "" });
        setBase64VideoFileToCrop(null);
        setBase64ImageToCrop(null);
        setCroppedBlob(null);

        if (isBase64Image(file)) {
            setBase64ImageToCrop(file);
            setShowCropperModal(true);
        }
        if (isBase64Video(file)) {
            setBase64VideoFileToCrop(file);
            setShowCropperModal(true);
        }
    };

    const onCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
        setSelectedSubcategory("subcategory");
        setSelectedTopic("topic");
        setSelectedSubtopic("subtopic");
    };

    const onSubcategoryChange = (e) => {
        setSelectedSubcategory(e.target.value);
        setSelectedTopic("topic");
        setSelectedSubtopic("subtopic");
        setErrors({ ...errors, [e.target.name]: "" });
    };

    const onTopicChange = (e) => {
        setSelectedTopic(e.target.value);
        setSelectedSubtopic("subtopic");
        setErrors({ ...errors, [e.target.name]: "" });
    };

    const onSubtopicChange = (e) => {
        setSelectedSubtopic(e.target.value);
        setErrors({ ...errors, subcategory: "" });
    };

    const getSelectedDirectoryUuid = () => {
        if (selectedSubtopic !== "subtopic") return selectedSubtopic;
        if (selectedTopic !== "topic") return selectedTopic;
        return selectedSubcategory;
    };

    const onTitleChange = (e) => {
        setTitle(e.target.value);
        setErrors({ ...errors, [e.target.name]: "" });
    };

    const onHtmlDescriptionChange = (e) => {
        setHtmlDescription(e);
        setErrors({ ...errors, description: "" });
    };

    // Manual validation
    const validate = () => {
        const newErrors = {};
        if (!title.trim()) newErrors.title = "Title is required";
        if (htmlDescription === emptyContentHtml) newErrors.description = "Description is required";
        if (!getSelectedDirectoryUuid().trim() || selectedSubcategory === "subcategory") newErrors.subcategory = "Sub-category is required";
        if (!uploadFile) newErrors.uploadFile = "Image or video file is required";
        if (canUseCheckedIn && checkIn && !hasValidCheckIn(checkIn)) newErrors.checkIn = "Select a location to finish this Check In, or remove it.";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors); // display errors
            return;
        }
        setIsLoading(true);
        const payload = {
            subcategoryUuid: getSelectedDirectoryUuid(),
            title: title,
            description: htmlToCaption(htmlDescription),
            richTextDescription: htmlDescription,
            recipeTags: []
        };
        if (canUseCheckedIn) {
            payload.checkIn = checkIn ? normaliseCheckInForRequest(checkIn) : null;
        }

        let uploadedAttachments = [];
        if (uploadedAttachmentFiles.length > 0) {
            try {
                uploadedAttachments = await Promise.all(
                    uploadedAttachmentFiles.map(async (attachment) => {
                        const fileExtension = attachment.file.type.split("/").pop();
                        const response = await s3PresignedContentUrl(
                            attachment.file,
                            fileExtension,
                            "attachment"
                        );
                        return {
                            uuid: response.uuid,
                            fileName: attachment.name,
                            fileUrl: response.storedUrl.split("?")[0],
                            fileType: attachment.type,
                        };
                    })
                );
            } catch {
                Toast.error("Failed to upload attachments.");
                setIsLoading(false);
                return;
            }
        }

        if (!uploadFile) {
            return;
        }

        let savedContent;
        if (fileType === "image") {
            const image = croppedBlob;
            const fileExtension = image.type.split("/").pop();
            try {
                const response = await s3PresignedContentUrl(image, fileExtension, fileType);
                const imageUrl = response?.storedUrl.split("?")[0];

                payload.uuid = response?.uuid;
                payload.image = {
                    imageUrl: imageUrl,
                    width: dimensions.width,
                    height: dimensions.height
                };
            } catch (error) {
                console.log("Error:", error);
            }
            try {
                savedContent = await saveContentWithImage(payload);

                const contentUuid = savedContent?.companyBodyAndMindPostImage.uuid;
                if (uploadedAttachments.length > 0 && contentUuid) {
                    try {
                        await saveAttachments(contentUuid, uploadedAttachments);
                    } catch (error) {
                        console.log("Error saving attachments:", error);
                        Toast.error("Attachments failed to save.");
                    }
                }

                if (savedContent) Toast.success("Content has been successfully added.");
                setIsLoading(false);
                toggleAddContentModal();
            } catch (error) {
                setIsLoading(false);
                console.log("Error:", error);
                Toast.error(error.getApiErrorDetails().message);
            }
            return;
        }

        const fileExtension = croppedVideo.type.split("/").pop();
        try {
            // Include crop/trim
            if (useCrop) {
                payload.crop = {
                    x: useCrop.crop?.croppedAreaPixels?.x ?? 0,
                    y: useCrop.crop?.croppedAreaPixels?.y ?? 0,
                    width: useCrop.crop?.croppedAreaPixels?.width ?? 0,
                    height: useCrop.crop?.croppedAreaPixels?.height ?? 0
                };
                payload.trimStart = useCrop.trimStart ?? 0;
                payload.trimEnd = useCrop.trimEnd ?? 0;
            }

            const response = await s3PresignedContentUrl(croppedVideo, fileExtension, fileType);
            const videoUrl = response?.storedUrl.split("?")[0];

            payload.uuid = response?.uuid;
            payload.video = {
                uuid: response?.uuid,
                previewImageUrl: "",
                videoUrl: videoUrl,
                aspectRatio: useCrop ? useCrop.aspectRatio : "widescreen",
            };

        } catch (error) {
            console.log(error);
        }

        try {
            savedContent = await saveContentWithVideo(payload);
            const contentUuid = savedContent?.companyBodyAndMindPostVideo?.uuid;

            if (uploadedAttachments.length > 0 && contentUuid) {
                try {
                    await saveAttachments(contentUuid, uploadedAttachments);
                } catch (error) {
                    console.log("Error saving attachments:", error);
                    Toast.error("Attachments failed to save.");
                }
            }

            if (savedContent) Toast.success("Content has been successfully added.");
            setIsLoading(false);
            toggleAddContentModal();
        } catch (error) {
            setIsLoading(false);
            console.log("Error:", error);
            Toast.error(error.getApiErrorDetails().message);
        }
        return;
    };

    const handleRemoveVideo = (e) => {
        e?.stopPropagation();
        setUploadFile(null);
        setBase64VideoFileToCrop(null);
        setBase64ImageToCrop(null);
        setCroppedBlob(null);
        setUseCrop({});
        uploadMediaRef.current?.triggerFilePicker();
    };

    return (
        <>
            <StandardModal
                open={true}
                onClose={toggleAddContentModal}
                title="Add Content"
                description="Enter a title and description, upload an image or video, and assign it to the right category, sub-category, topic, or sub-topic."
                widthMode="wide"
                bodyPadding="1.2rem 1.6rem 2rem"
                footer={(
                    <StandardModalActionGroup $pushRight>
                        <Button
                            disabled={isLoading}
                            type="button"
                            onClick={() => toggleAddContentModal()}
                            border="1px solid #000"
                            color="#000"
                            width="140px"
                            height="38px"
                            style={{ borderRadius: "7px", boxShadow: "none" }}
                        >
                            Close
                        </Button>
                        {isLoading ?
                            <LoadingIndicatorButton>
                                <LottieLoadingIndicator height="2rem" width="2rem" margin="5px" containerHeight="2rem" />
                            </LoadingIndicatorButton>
                            :
                            <Button
                                type="submit"
                                form={AddContentFormId}
                                backgroundColor="#171A22"
                                border="1px solid #171A22"
                                color="#fff"
                                width="140px"
                                height="38px"
                                style={{ borderRadius: "7px", boxShadow: "none" }}
                            >
                                Save
                            </Button>}
                    </StandardModalActionGroup>
                )}
            >
                <AddContentForm id={AddContentFormId} onSubmit={handleSubmit}>
                    <AddContentPrimaryGrid>
                        <StandardModalSection>
                            <ContentForm
                                title={title}
                                onTitleChange={onTitleChange}
                                htmlDescription={htmlDescription}
                                setHtmlDescription={onHtmlDescriptionChange}
                                errors={errors}
                            />
                        </StandardModalSection>
                        <StandardModalSection>
                            <MediaColumn>
                                <StandardModalSectionTitle>Upload Image/Video</StandardModalSectionTitle>
                                <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
                                    <UploadMedia
                                        ref={uploadMediaRef}
                                        onFileSelect={setOnUploadFileChange}
                                        allFileType={true}
                                        setDimensions={setDimensions}
                                        fileType={fileType}
                                        setFileType={setFileType}
                                        file={uploadFile}
                                        cropperResults={useCrop}
                                        boxStyle={{
                                            width: "100%",
                                            height: "auto",
                                            aspectRatio: "16 / 9",
                                            containerWidth: "100%",
                                            borderRadius: "8px"
                                        }}
                                        buttonStyle={{
                                            backgroundColor: "#171A22",
                                            border: "1px solid #171A22",
                                            borderRadius: "8px",
                                            boxShadow: "none",
                                            color: "#FFFFFF",
                                            fontFamily: "Manrope, Helvetica, sans-serif",
                                            fontWeight: 700,
                                            alignSelf: "flex-start"
                                        }}
                                    />
                                    {uploadFile && (
                                        <RemoveMediaButton type="button" onClick={handleRemoveVideo} style={{ top: "-8px" }}>
                                            <img src={CloseIcon} alt="close button" width={24} height={24} />
                                        </RemoveMediaButton>
                                    )}
                                </div>
                                <TextLabel color="red" fontSize="0.8125rem" fontWeight="500" style={{ marginTop: 0 }}>{errors.uploadFile}</TextLabel>
                            </MediaColumn>
                        </StandardModalSection>
                    </AddContentPrimaryGrid>
                    <StandardModalSection>
                        <Attachment
                            uploadedAttachmentFiles={uploadedAttachmentFiles}
                            setUploadedAttachmentFiles={setUploadedAttachmentFiles}
                            deleteAttachmentFiles={deleteAttachmentFiles}
                            setDeleteAttachmentFiles={setDeleteAttachmentFiles}
                        />
                    </StandardModalSection>
                    {canUseCheckedIn && (
                        <BodyAndMindCheckInSection
                            value={checkIn}
                            onChange={(nextCheckIn) => {
                                setCheckIn(nextCheckIn);
                                setErrors(current => ({ ...current, checkIn: "" }));
                            }}
                            error={errors.checkIn}
                        />
                    )}
                    <StandardModalSection>
                        <StandardModalSectionHeader>
                            <div>
                                <StandardModalSectionTitle>Select Content Location</StandardModalSectionTitle>
                                <StandardModalDescription>
                                    Choose the category first, then narrow the location as needed.
                                </StandardModalDescription>
                            </div>
                        </StandardModalSectionHeader>

                        <CategoryGrid>
                            <DirectorySelection
                                categories={companyCategoriesList}
                                directories={subcategoriesList}
                                selectedCategory={selectedCategory}
                                selectedSubcategory={selectedSubcategory}
                                selectedTopic={selectedTopic}
                                selectedSubtopic={selectedSubtopic}
                                onCategoryChange={onCategoryChange}
                                onSubcategoryChange={onSubcategoryChange}
                                onTopicChange={onTopicChange}
                                onSubtopicChange={onSubtopicChange}
                                error={errors.subcategory}
                                directoriesAreLoading={subcategoriesListIsFetching}
                            />
                        </CategoryGrid>
                    </StandardModalSection>
                </AddContentForm>
            </StandardModal>
            {base64ImageToCrop && (
                <ImageCropModal
                    imageSrc={base64ImageToCrop}
                    isOpen={showCropperModal}
                    onClose={(shouldClear) => {
                        setShowCropperModal(false);
                        if (shouldClear) {
                            setUploadFile(null);
                            setBase64ImageToCrop(null);
                            setFileType(null);
                        }
                    }}
                    onCropComplete={onImageCropComplete}
                    aspectRatios={[
                        { width: 16, height: 9, label: "16:9", standardName: ASPECT_RATIO_LABEL.WIDESCREEN },
                    ]}
                    allowedOrientations={["landscape"]}
                    lockedAspectRatio={undefined}
                />
            )}
            {base64VideoFileToCrop && (
                <VideoCropModal
                    videoSrc={base64VideoFileToCrop}
                    isOpen={true}
                    onClose={(shouldClear) => {
                        setBase64VideoFileToCrop(null);
                        if (shouldClear) {
                            handleRemoveVideo();
                        }
                    }}
                    onCropComplete={(file, crop, trimStart, trimEnd, aspectRatioLabel) =>
                        onVideoCropComplete(file, crop, trimStart, trimEnd, aspectRatioLabel)
                    }
                    aspectRatios={[
                        { width: 16, height: 9, label: "16:9", standardName: ASPECT_RATIO_LABEL.WIDESCREEN },
                    ]}
                    allowedOrientations={["landscape"]}
                    lockedAspectRatio={undefined}
                />
            )}
        </>
    );
}

export default AddContent;
